/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectPmsSync,
  selectTargetDate,
  selectPaceHorizon,
  selectPaceVariable,
  selectHotelName,
  selectForecastFilter,
  selectEnableHotelGroups,
  selectReportType,
  selectIsDemoUser,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import {
  endOfMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSaturday,
  isSunday,
  lastDayOfYear,
  nextSaturday,
  previousSunday,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { PACE_HORIZON_SELECTION, REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import {
  getAlignmentAndDateComparisonSchema,
  getBookingDateSchema,
  getComparisonBookingDateSchema,
  getComparisonStayDateSchema,
  getDashboardPmsSyncSchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupOrderSchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getIsForecastSchema,
  getMaxStayDateSchema,
  getNormalizedViewSchema,
  getPaceGranularitySchema,
  getPaceVariableSchema,
  getPaceWidgetPaceHorizonSchema,
  getPmsSyncSchema,
  getRevenueForecastSchema,
  getRoomForecastSchema,
  getSegmentFocusOnSchema,
  // getSymmetricComparisonSchema,
  getTargetDateSchema,
  getTimeSegmentBreakdownAndFocusOnSchema,
  percentageSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import {
  selectIsForecast,
  selectPaceAlignment,
  selectPaceAlignmentToggle,
  selectPaceDateComparison,
  selectPaceSymmetricComparisonToggle,
  selectPaceTimeOccupiedRoomFilterList,
  selectPaceTimeRevenueFilterList,
  selectPercentage,
  selectPaceBookingPaceWindow,
  selectPaceBookingDate,
  selectTabularView,
  selectAdjustedStartDate,
  selectAdjustedEndDate,
  selectGranularity,
  selectPaceTimeTabularFilterList,
  selectPaceFocusOn,
  selectPaceChartBreakdown,
  selectIsPaceDetailed,
  selectIsFocusOnSet,
  selectActivePaceTab,
  selectPaceHotelSegmentTabularFilterList,
  selectPaceTimeSegmentTabularFilterList,
  selectPaceHotelSegmentRevenueFilterList,
  selectPaceHotelSegmentOccupiedRoomsFilterList,
  selectPaceTimeSegmentRevenueFilterList,
  selectPaceTimeSegmentOccupiedRoomsFilterList,
  selectHotelFocusOn,
  selectIsPaceSegmentTimeDeAggregate,
  selectEnableSecondaryHotelGroups,
  selectNormalizedView,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
} from 'modules/dashboard/components/tab-container/pace-tabs/selectors';
import { paceActions } from 'modules/dashboard/components/tab-container/pace-tabs/slice';
import writeLog from 'modules/common/utils/filter-log';
import { getGranularity } from '../functions';
import { GRANULARITY } from '../constants';
import { getSelectedBreakdown } from '../../../../functions';
import calculateComparisonDates, {
  hasLeapDay,
} from '../../../../../common/utils/dateComparisonUtils';

/**
 * Custom hook to generate summary widget filter schemas
 */
const usePaceTimeWidget = () => {
  const dispatch = useDispatch();
  //
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const forecastFilter = useSelector(selectForecastFilter);
  const bookingDate = useSelector(selectPaceBookingDate);
  const dateComparison = useSelector(selectPaceDateComparison);
  const percentage = useSelector(selectPercentage);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const alignment = useSelector(selectPaceAlignment);
  const alignmentToggle = useSelector(selectPaceAlignmentToggle);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const reportType = useSelector(selectReportType);
  const revenueFilterList = useSelector(selectPaceTimeRevenueFilterList);
  const occupiedRoomsFilterList = useSelector(selectPaceTimeOccupiedRoomFilterList);
  const tabularFilterList = useSelector(selectPaceTimeTabularFilterList);
  const isPaceForecast = useSelector(selectIsForecast);
  const bookingPaceWindow = useSelector(selectPaceBookingPaceWindow);
  const isTabular = useSelector(selectTabularView);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const granularity = useSelector(selectGranularity);
  const focusOn = useSelector(selectPaceFocusOn);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const isDetailed = useSelector(selectIsPaceDetailed);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const activeTab = useSelector(selectActivePaceTab);
  const hotelSegmentRevenueFilterList = useSelector(selectPaceHotelSegmentRevenueFilterList);
  const hotelSegmentOccupiedRoomsFilterList = useSelector(
    selectPaceHotelSegmentOccupiedRoomsFilterList
  );
  const hotelSegmentTabularFilterList = useSelector(selectPaceHotelSegmentTabularFilterList);
  const timeSegmentRevenueFilterList = useSelector(selectPaceTimeSegmentRevenueFilterList);
  const timeSegmentOccupiedRoomsFilterList = useSelector(
    selectPaceTimeSegmentOccupiedRoomsFilterList
  );
  const timeSegmentTabularFilterList = useSelector(selectPaceTimeSegmentTabularFilterList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const isPaceSegmentTimeDeAggregate = useSelector(selectIsPaceSegmentTimeDeAggregate);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const isDemoUser = useSelector(selectIsDemoUser);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);

  //
  useEffect(() => {
    if (
      !IsFocusOnSet ||
      reportType !== REPORT_TYPE.PACE ||
      !dateComparison ||
      !alignment ||
      !paceHorizon ||
      !hotelId ||
      !visualFilters
    ) {
      return;
    }
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    let startDate;
    let endDate;
    if (isTabular) {
      if (granularity === GRANULARITY.DAILY) {
        startDate = bookingDate?.startDate;
        endDate = bookingDate?.endDate;
      } else {
        startDate = adjustedStartDate;
        endDate = adjustedEndDate;
      }
    } else {
      startDate = bookingDate?.startDate;
      endDate = bookingDate?.endDate;
    }
    const bookingDateFilter = getBookingDateSchema(visualFilters, startDate, endDate);
    //
    const paceTimeRevenueVariableFilter = getPaceVariableSchema(
      visualFilters,
      'Revenue',
      TAGS.SUMMARY_PACE_VARIABLE
    );
    const paceTimeOccupiedRoomsVariableFilter = getPaceVariableSchema(
      visualFilters,
      'Occupied Rooms',
      TAGS.SUMMARY_PACE_VARIABLE
    );
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);
    const percentageFilter = percentageSchema(visualFilters, percentage);

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);
    let hotelListIds = [];
    let hotelGroup1Filter;
    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);
    //
    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    let hotelIdFilter = [];
    if (enableHotelGroups) {
      // const hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode);
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    // const symmetricComparisonFilter = getSymmetricComparisonSchema(
    //   visualFilters,
    //   symmetricComparisonToggle
    // );
    const paceHorizonFilter = getPaceWidgetPaceHorizonSchema(
      visualFilters,
      paceHorizon,
      bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON
    );
    const paceVariableFilter = getPaceVariableSchema(
      visualFilters,
      paceVariable,
      TAGS.PACE_VARIABLE
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.PACE_WIDGET);
    const isForecastFilter = getIsForecastSchema(visualFilters, isPaceForecast);
    const granularityFilter = getPaceGranularitySchema(visualFilters, granularity);
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const deAggregateSegmentAndFocusOnFilter = getTimeSegmentBreakdownAndFocusOnSchema(
      visualFilters,
      chartBreakdown,
      isDetailed,
      focusOn,
      hotelFocusOn?.id === hotelId?.id
    );
    // const groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
    // let hotelGroup1Filter;
    // const hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups && hotelListIds?.length > 0) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups && groupFocusOnIds?.length > 0) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];

    const hotelGroupOrderFilter = getHotelGroupOrderSchema(visualFilters, isDemoUser);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    //
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      targetDate,
      { startDate, endDate },
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate }, // not used in Pace
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    //
    const hasLeap = hasLeapDay(adjustedStayDate?.startDate, adjustedStayDate?.endDate);
    dispatch(paceActions.setIsPaceLeapComparison(hasLeap));
    //
    if (
      paceTimeRevenueVariableFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      paceTimeOccupiedRoomsVariableFilter &&
      percentageFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      paceHorizonFilter &&
      paceVariableFilter &&
      alignmentAndDateComparisonFilter &&
      // symmetricComparisonFilter &&
      pmsSyncFilter &&
      isForecastFilter &&
      bookingDateFilter &&
      granularityFilter &&
      segmentFocusOnFilter &&
      hotelFilters &&
      hotelGroupOrderFilter &&
      normalizedFilter
    ) {
      const filters = [
        roomForecastFilter,
        revenueForecastFilter,
        percentageFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        //  symmetricComparisonFilter,
        hotelIdFilter,
        paceHorizonFilter,
        paceVariableFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      //
      const paceTimeRevenueFilterList = [paceTimeRevenueVariableFilter, ...filters];
      const paceTimeOccupiedFilterList = [paceTimeOccupiedRoomsVariableFilter, ...filters];
      if (JSON.stringify(revenueFilterList) !== JSON.stringify(paceTimeRevenueFilterList)) {
        //
        writeLog('Pace Time Revenue Filters', paceTimeRevenueFilterList);
        dispatch(paceActions.setPaceTimeRevenueFilterList(paceTimeRevenueFilterList));
      }
      if (JSON.stringify(occupiedRoomsFilterList) !== JSON.stringify(paceTimeOccupiedFilterList)) {
        //
        writeLog('Pace Time Occupied Rooms Filters', paceTimeOccupiedFilterList);
        dispatch(paceActions.setPaceTimeOccupiedFilterList(paceTimeOccupiedFilterList));
      }
      // tabular filter list

      const commonTabularFilterList = [
        roomForecastFilter,
        revenueForecastFilter,
        percentageFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        paceHorizonFilter,
        paceVariableFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        granularityFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (JSON.stringify(tabularFilterList) !== JSON.stringify(commonTabularFilterList)) {
        //
        writeLog('Pace Time Tabular Filters', commonTabularFilterList);
      }
      dispatch(paceActions.setPaceTimeTabularFilterList(commonTabularFilterList));

      const paceTimeSegmentRevenueList = [
        ...deAggregateSegmentAndFocusOnFilter,
        paceTimeRevenueVariableFilter,
        ...filters,
      ];
      const paceTimeSegmentOccupiedRoomsList = [
        ...deAggregateSegmentAndFocusOnFilter,
        paceTimeOccupiedRoomsVariableFilter,
        ...filters,
      ];

      // pace time segment filter list
      const paceTimeSegmentTabularList = [
        roomForecastFilter,
        revenueForecastFilter,
        percentageFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        paceHorizonFilter,
        paceVariableFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        granularityFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        ...deAggregateSegmentAndFocusOnFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];

      // pace hotel segment filter list
      const hotelFilterList = [
        roomForecastFilter,
        revenueForecastFilter,
        targetDateFilter,
        paceHorizonFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        ...hotelFilters,
        isForecastFilter,
        hotelGroupOrderFilter,
        normalizedFilter,
        ...segmentFocusOnFilter,
        ...pmsSyncSchema,
      ];

      // tabular pace hotel segment filter list
      const tabularHotelFilterList = [
        targetDateFilter,
        paceHorizonFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        ...hotelFilters,
        hotelGroupOrderFilter,
        normalizedFilter,
        granularityFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        ...deAggregateSegmentAndFocusOnFilter,
      ];

      const paceHotelSegmentRevenueList = [
        ...deAggregateSegmentAndFocusOnFilter,
        paceTimeRevenueVariableFilter,
        ...hotelFilterList,
      ];
      const paceHotelSegmentOccupiedRoomsList = [
        ...deAggregateSegmentAndFocusOnFilter,
        paceTimeOccupiedRoomsVariableFilter,
        ...hotelFilterList,
      ];

      if (
        activeTab === REPORT_TYPE.PACE_SEGMENT_WIDGET &&
        isPaceSegmentTimeDeAggregate &&
        hotelFocusOn?.id !== hotelId?.id
      ) {
        dispatch(paceActions.setPaceHotelSegmentRevenueFilterList([]));
        dispatch(paceActions.setPaceHotelSegmentOccupiedFilterList([]));
        dispatch(paceActions.setPaceHotelSegmentTabularFilterList([]));
        //
        dispatch(paceActions.setPaceHotelSegmentRevenueFilterList(paceHotelSegmentRevenueList));
        dispatch(
          paceActions.setPaceHotelSegmentOccupiedFilterList(paceHotelSegmentOccupiedRoomsList)
        );
        dispatch(paceActions.setPaceHotelSegmentTabularFilterList(tabularHotelFilterList));
      }

      if (
        JSON.stringify(paceHotelSegmentRevenueList) !==
        JSON.stringify(hotelSegmentRevenueFilterList)
      ) {
        writeLog('Pace Hotel Segment Revenue Filters', paceHotelSegmentRevenueList);
      }
      if (
        JSON.stringify(paceHotelSegmentOccupiedRoomsList) !==
        JSON.stringify(hotelSegmentOccupiedRoomsFilterList)
      ) {
        writeLog('Pace Hotel Segment Occupied Rooms Filters', paceHotelSegmentOccupiedRoomsList);
      }
      if (
        JSON.stringify(tabularHotelFilterList) !== JSON.stringify(hotelSegmentTabularFilterList)
      ) {
        writeLog('Pace Hotel Segment Tabular Filters', tabularHotelFilterList);
      }

      //

      if (
        JSON.stringify(paceTimeSegmentRevenueList) !== JSON.stringify(timeSegmentRevenueFilterList)
      ) {
        writeLog('Pace Time Segment Revenue Filters', paceTimeSegmentRevenueList);
        dispatch(paceActions.setPaceTimeSegmentRevenueFilterList(paceTimeSegmentRevenueList));
      }
      if (
        JSON.stringify(paceTimeSegmentOccupiedRoomsList) !==
        JSON.stringify(timeSegmentOccupiedRoomsFilterList)
      ) {
        writeLog('Pace Time Segment Occupied Rooms Filters', paceTimeSegmentOccupiedRoomsList);
        dispatch(
          paceActions.setPaceTimeSegmentOccupiedFilterList(paceTimeSegmentOccupiedRoomsList)
        );
      }
      if (
        JSON.stringify(paceTimeSegmentTabularList) !== JSON.stringify(timeSegmentTabularFilterList)
      ) {
        writeLog('Pace Time Segment Tabular Filters', paceTimeSegmentTabularList);
        dispatch(paceActions.setPaceTimeSegmentTabularFilterList(paceTimeSegmentTabularList));
      }
      // }
    }
  }, [
    pmsSyncToggle,
    visualFilters,
    forecastFilter,
    targetDate,
    hotelId,
    paceHorizon,
    paceVariable,
    percentage,
    alignment,
    symmetricComparisonToggle,
    dateComparison,
    alignmentToggle,
    enableHotelGroups,
    reportType,
    isPaceForecast,
    bookingDate,
    bookingPaceWindow,
    isTabular,
    adjustedStartDate,
    adjustedEndDate,
    granularity,
    focusOn,
    chartBreakdown,
    isDetailed,
    IsFocusOnSet,
    isPaceSegmentTimeDeAggregate,
    activeTab,
    hotelFocusOn,
    enableSecondaryHotelGroups,
    isDemoUser,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    resetBookingDate,
  ]);

  // Triggered when booking date value changes
  useEffect(() => {
    // Set booking dates based on granularity
    const paceGranularity = getGranularity(
      bookingDate,
      paceHorizon,
      bookingPaceWindow,
      visualFilters
    );
    dispatch(paceActions.setGranularity(paceGranularity));
    if (paceGranularity === GRANULARITY.WEEKLY) {
      if (isSunday(new Date(bookingDate.startDate))) {
        dispatch(paceActions.setAdjustedStartDate(new Date(bookingDate.startDate).toISOString()));
      } else {
        dispatch(
          paceActions.setAdjustedStartDate(
            previousSunday(new Date(bookingDate.startDate)).toISOString()
          )
        );
      }
      if (isSaturday(new Date(bookingDate.endDate))) {
        dispatch(paceActions.setAdjustedEndDate(new Date(bookingDate.endDate).toISOString()));
      } else {
        dispatch(
          paceActions.setAdjustedEndDate(nextSaturday(new Date(bookingDate.endDate)).toISOString())
        );
      }
    } else if (paceGranularity === GRANULARITY.MONTHLY) {
      if (isFirstDayOfMonth(new Date(bookingDate.startDate))) {
        dispatch(paceActions.setAdjustedStartDate(new Date(bookingDate.startDate).toISOString()));
      } else {
        dispatch(
          paceActions.setAdjustedStartDate(
            startOfMonth(new Date(bookingDate.startDate)).toISOString()
          )
        );
      }
      if (isLastDayOfMonth(new Date(bookingDate.endDate))) {
        dispatch(paceActions.setAdjustedEndDate(new Date(bookingDate.endDate).toISOString()));
      } else {
        dispatch(
          paceActions.setAdjustedEndDate(endOfMonth(new Date(bookingDate.endDate)).toISOString())
        );
      }
    } else if (paceGranularity === GRANULARITY.YEARLY) {
      if (
        isSameDay(startOfYear(new Date(bookingDate.startDate)), new Date(bookingDate.startDate))
      ) {
        dispatch(paceActions.setAdjustedStartDate(new Date(bookingDate.startDate).toISOString()));
      } else {
        dispatch(
          paceActions.setAdjustedStartDate(
            startOfYear(new Date(bookingDate.startDate)).toISOString()
          )
        );
      }
      if (isSameDay(lastDayOfYear(new Date(bookingDate.endDate)), new Date(bookingDate.endDate))) {
        dispatch(paceActions.setAdjustedEndDate(new Date(bookingDate.endDate).toISOString()));
      } else {
        dispatch(
          paceActions.setAdjustedEndDate(lastDayOfYear(new Date(bookingDate.endDate)).toISOString())
        );
      }
    }
  }, [bookingDate, paceHorizon, bookingPaceWindow, visualFilters]);
};
//
export default usePaceTimeWidget;
